import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Stack, Typography, Tooltip, Button, Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader/PageHeader';
import MultiVotingCard from '../FeedbackCollation/MultiVotingCard';
import VotingFilter from '../FeedbackCollation/VotingFilters';
import { useOkta } from '../../hooks/oktaHook';
import DecisionTable from './DecisionTable';
import {
  setDecisionVotingTableData,
  setTagUsers,
  setRecommendationOptions,
  setEditedRows,
  setStepperStatus,
  setPrevTableData,
  setPrevEditedRows
} from '../../redux/DecisionCentral/decisionCentralRulesSlice';
import {
  fetchUsers,
  fetchDecisionData,
  fetchRecommendationOptions,
  saveDecisionData,
  saveCompletionData,
  fetchUploadsInProgressByUserId,
  fetchUploadsInProgress
} from './services';
import { getStepStatus } from '../FeedbackCollation/servies';
import {
  finalDecisions,
  getColumns,
  getData,
  restructureData,
  decisionCentral
} from './utils';
import decisionMakingIcon from '../../assests/images/decisionMakingCentral.svg';
import { triggerNotification } from '../../utils/NotificationContext';
import { TPO_ADMIN, CORE_USER } from '../../utils/Constants';
import { getSanitizedMemberList } from '../SetupTeam/ExcludeMembers';
import { getUrlWithParams } from '../../utils/Utils';

const DecisionMakingCentral = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const intervalIdRef = useRef(null);
  const { search } = useLocation();
  const newParams = new URLSearchParams(search);
  const selectedCycleId = Number(newParams.get('cycleId'));
  const selectedCycle = newParams.get('cycleName');
  const bu = newParams.get('bu');
  const { userDetails } = useOkta();
  const decisionCentralRules = useSelector((state) => state.decisionMakingCentralRules);
  const {
    recommendationOptions,
    tagDecisionUsers,
    votingTableData,
    editedRows = [],
    prevTableData = []
  } = decisionCentralRules;

  const { roles, gpid, email } = userDetails;
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [checkedRows, setCheckedRows] = useState([]);
  const [vote, setVote] = useState({});
  const [areVotesApplied, setAreVotesApplied] = useState(false);
  const [areVotesSaved, setAreVotesSaved] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isDecision, setIsDecision] = useState(false);
  // Final view Screen state
  const [isDCMCompleted, setIsDCMCompleted] = useState(false);
  const [isAnyFieldEmpty, setFiledEmpty] = useState(true);
  const [showUploadDownload, setShowUploadDownload] = useState(true);
  const [showDecisonSubmit, setshowDecisonSubmit] = useState(true);
  const [isAdmin, setIsAdmin] = useState(
    roles?.some((role) => [TPO_ADMIN, CORE_USER].includes(role))
  );
  const [showApply, setShowApply] = useState();
  const [showUndo, setShowUndo] = useState(true);
  const [userLevelUpload, setUserLevelUpload] = useState('');
  const [uploadStatusMsg, setUploadStatusMsg] = useState({});
  const perPageRowCount = 3000; //default per page row = 500

  const decisionColumnsToRemove = ['comment', 'decision'];
  const completionColumnsToRemove = [];
  const userRoles =
    JSON.parse(sessionStorage.getItem('tpoDefaultUserDetails') || '{}')?.roles || [];

  const initialSelectedFilters = {
    segment: '',
    brand: '',
    subbrand: '',
    sku: '',
    algoreco: '',
    assignedTo: ''
  };
  const [selectedFilters, setSelectedFilters] = useState(initialSelectedFilters);

  // Advance filter category
  const productAttr = [
    'tposku',
    'sku name',
    'brand',
    'segment',
    'sub-brand',
    'sub-category',
    'packSize',
    'upc/gtin'
  ];
  const perforamceMetric = [
    'revenue',
    'volume qty',
    'revenue growth',
    'volume growth',
    'profit %',
    'flowback %',
    'waste %',
    'complexity',
    'store count',
    'distribution %',
    'facility %'
  ];
  const operationalInp = ['channel importance', 'inventory', 'complexity'];
  let decisions = ['quadrant', 'steering team recommendation'];

  const multiVoteComponentLabels = {
    title: 'Decision',
    dropdown: 'Decision(Max 24 chars)*',
    date: 'Effective Date*',
    inputFrom: 'Owner',
    comment: 'Comments',
    buttonLabel: 'Submit Votes'
  };
  const commonColumnsToRemove = [
    'agreeDisagreeStatus',
    'recommendation',
    'taggedUsers',
    'ownerName',
    'effectiveDate',
    'ownerEmail',
    'instructionComment',
    'DecisionResponse',
    'voteAlign',
    'completionStatus',
    'ExecDate',
    'finalComment',
    'quadrant',
    'national classification',
    'action reco',
    'all regions'
  ];
  const generalColumnsToRemove = ['DecisionResponse', 'taggedUsers', 'decision'];
  const multiVoteCompleteComponentLabels = {
    title: 'Decision',
    date: 'Execution Date*',
    comment: 'Comments',
    buttonLabel: 'Apply'
  };

  const columnsToRemove = isDCMCompleted
    ? []
    : isCompleted
      ? completionColumnsToRemove
      : isDecision
        ? decisionColumnsToRemove
        : generalColumnsToRemove;

  const uniqueColumns = (columns) => {
    const seen = new Set();
    return columns
      .filter((col) => {
        const duplicate = seen.has(col.id);
        seen.add(col.id);
        return !duplicate;
      })
      .map((col) => {
        if (col.id === 'functional team comments') {
          return { ...col, label: 'FUNCTIONAL STAKEHOLDERS COLLATED COMMENTS' };
        }
        return col;
      });
  };

  const columns = uniqueColumns(
    getColumns(
      restructureData(votingTableData),
      '',
      '',
      isDecision || isCompleted ? false : true
    )?.filter((col) => ![...columnsToRemove, ...commonColumnsToRemove]?.includes(col.id))
  );

  const expandCols = [
    { id: 'decision', label: 'DECISION (MAX 24 CHARS) *', sticky: true, right: 564, width: 200 },
    {
      id: 'effectiveDate',
      label: 'EFFECTIVE DATE *',
      sticky: true,
      right: 394,
      width: 170,
      type: 'date'
    },
    { id: 'ownerEmail', label: 'OWNER', sticky: true, right: 192, width: 150 },
    { id: 'instructionComment', label: 'COMMENT', sticky: true, right: 0, width: 160 }
  ];

  const completionExpandCols = [
    { id: 'decision', label: 'DECISION', sticky: true, right: 564, width: 200 },
    {
      id: 'effectiveDate',
      label: 'EFFECTIVE DATE *',
      sticky: true,
      right: 394,
      width: 170,
      type: 'date'
    },
    { id: 'ownerEmail', label: 'OWNER', sticky: true, right: 192, width: 150 },
    { id: 'instructionComment', label: 'COMMENT', sticky: true, right: 0, width: 160 }
  ];

  const getUsers = useCallback(
    async (bu, userLevel) => {
      try {
        if (userLevel !== null && userLevel !== undefined && userLevel !== '') {
          const { content } = await fetchUsers(bu, userLevel);
          let filteredList = getSanitizedMemberList(content);
          dispatch(setTagUsers(filteredList));
        } else {
          console.log('No userlevel found!');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    },
    [dispatch]
  );

  const getRecommendationOptionData = useCallback(
    async (bu) => {
      try {
        const { content } = await fetchRecommendationOptions(bu);
        dispatch(setRecommendationOptions(content));
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    },
    [dispatch]
  );

  const getVoteData = async (voteData, checkedRows) => {
    const updatedRow = votingTableData.map((row) => {
      if (checkedRows.includes(row.CycleSKUKey)) {
        return {
          ...row,
          decision: voteData.recoName,
          instructionComment: voteData.comment,
          ownerEmail: voteData.selectedOptions[0]?.UserEmail,
          ownerName: voteData.selectedOptions[0]?.UserName,
          effectiveDate: new Date(voteData.effectiveDate).toISOString().slice(0, 10)
        };
      }
      return row;
    });
    dispatch(setPrevTableData(votingTableData));
    dispatch(setPrevEditedRows(editedRows));
    dispatch(setDecisionVotingTableData(updatedRow));
    dispatch(setEditedRows([...new Set([...editedRows, ...checkedRows])]));
  };
  const getVoteCompletionData = async (voteData, checkedRows) => {
    const updatedRow = votingTableData.map((row) => {
      if (checkedRows.includes(row.CycleSKUKey)) {
        return {
          ...row,
          completionStatus: voteData.thumbStatus,
          finalComment: voteData.comment,
          ExecDate: new Date(voteData.effectiveDate).toISOString().slice(0, 10)
        };
      }
      return row;
    });
    dispatch(setPrevTableData(votingTableData));
    dispatch(setPrevEditedRows(editedRows));
    dispatch(setDecisionVotingTableData(updatedRow));
    dispatch(setEditedRows([...new Set([...editedRows, ...checkedRows])]));
  };
  const getUserLevelInfo = async (bu) => {
    const cycleId = selectedCycleId || Number(localStorage?.getItem('cycle_id'));
    if (cycleId) {
      try {
        const response = await getStepStatus(gpid, roles[0], bu, cycleId);
        const { userLevel, finalDecision, executeActions } = response;
        const stepCompletionStatus = finalDecision?.status === 'Completed';
        const stepDecisionStatus = finalDecision?.status === 'In Progress';

        const stepperNotStarted =
          executeActions?.status === 'Not Started' && finalDecision?.status === 'Not Started';

        const stepperCompleted =
          executeActions?.status === 'Completed' && finalDecision?.status === 'Completed';

        setIsCompleted(stepCompletionStatus);
        setIsDCMCompleted(stepperCompleted);
        setIsDecision(stepDecisionStatus);
        dispatch(setStepperStatus(response));
        await getUsers(bu, userLevel);
        await getRecommendationOptionData(bu);

        let fetchData = null;
        if (stepperNotStarted) {
          fetchData = 999;
        } else if (stepperCompleted) {
          fetchData = 1001;
        } else {
          fetchData = stepCompletionStatus ? 1000 : 999;
        }
        setUserLevelUpload(fetchData);
        const allData = [];
        let shouldContinue = true;
        let pageNo = 0;
        setIsTableLoading(true);
        while (shouldContinue) {
          const decisionResponse = await fetchDecisionData(
            bu,
            gpid,
            fetchData,
            pageNo,
            perPageRowCount,
            selectedCycleId
          );
          const { totalRecordsCountForPagination, content, totalPagesCountForPagination, error } =
            decisionResponse;
          if (error) {
            triggerNotification(error?.message, 'error');
            return;
          }
          allData.push(...content);
          if (totalPagesCountForPagination === pageNo) {
            shouldContinue = false;
          } else {
            pageNo++;
          }
        }

        const mappedData = allData.map((row) => ({
          ...row,
          decision: row?.DecisionResponse?.decision || row.decision,
          effectiveDate: row?.DecisionResponse?.effectiveDate,
          ownerEmail: row?.DecisionResponse?.ownerEmail || '',
          ownerName: row?.DecisionResponse?.ownerName || '',
          instructionComment: row?.DecisionResponse?.instructionComment,
          voteAlign: row?.DecisionResponse?.voteAlign,
          completionStatus:
            row?.DecisionResponse?.completionStatus === undefined
              ? null
              : row?.DecisionResponse?.completionStatus,
          finalComment: row?.DecisionResponse?.finalComment,
          ExecDate: row?.DecisionResponse?.ExecDate
        }));
        dispatch(setDecisionVotingTableData(getData(mappedData, 'Decision Table')));
        dispatch(setPrevTableData(getData(mappedData, 'Decision Table')));
      } catch (error) {
        setIsTableLoading(false);
      } finally {
        setIsTableLoading(false);
      }
    }
  };
  useEffect(() => {
    dispatch(setEditedRows([]));
  }, []);
  useEffect(() => {
    if (bu && gpid) {
      getUserLevelInfo(bu);
    }
  }, [bu, gpid]);
  useEffect(() => {
    if (checkedRows.length > 0) {
      setShowUndo(true);
    }
  }, [checkedRows]);

  const resetVoteData = async () => {
    setCheckedRows([]);
    dispatch(setDecisionVotingTableData(prevTableData));
    setAreVotesApplied((prev) => !prev);
  };
  useEffect(() => {
    if (roles) {
      setIsAdmin(roles?.some((role) => [CORE_USER, TPO_ADMIN].includes(role)));
    }
  }, [roles]);
  useEffect(() => {
    let isEmpty = false;
    const rows = votingTableData.filter((row) => editedRows.includes(row.CycleSKUKey));

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (
        row?.instructionComment?.length > 0 &&
        (row?.decision === null || row?.decision === undefined || row?.decision === '') &&
        (row?.ownerEmail === null || row?.ownerEmail === undefined || row?.ownerEmail === '') &&
        (row?.ownerName === null || row?.ownerName === undefined || row?.ownerName === '') &&
        (row?.effectiveDate === null ||
          row?.effectiveDate === undefined ||
          row?.effectiveDate === '')
      ) {
        continue;
      }
      if (isDecision) {
        isEmpty = [
          row?.CycleSKUKey,
          row?.decision,
          // row?.ownerEmail,
          // row?.ownerName,
          row?.effectiveDate
        ].some((field) => field === null || field === undefined || field === '');
      } else if (isCompleted) {
        isEmpty = [row?.CycleSKUKey, row?.completionStatus, row?.ExecDate].some(
          // isEmpty = [row?.CycleSKUKey, row?.completionStatus, row?.effectiveDate].some(
          (field) => field === null || field === undefined || field === ''
        );
      }

      if (isEmpty) {
        break; // Exit the loop early if any field is empty
      }
    }

    setFiledEmpty(isEmpty);
  }, [editedRows, votingTableData, isDecision, isCompleted]);

  useEffect(() => {
    if ((isAdmin && isDecision) || isCompleted || userRoles.includes(CORE_USER) || userRoles.includes(TPO_ADMIN)) {
      setShowUploadDownload(false);
    }
  }, [isAdmin, isCompleted, isDecision, userRoles]);
  const saveDecisionDataRows = async () => {
    try {
      // Filter the votingTableData based on CycleSKUKey present in editedRows
      const editedData = votingTableData
        .filter((row) => editedRows.includes(row.CycleSKUKey))
        .map((row) => ({
          cycleSKUKey: row?.CycleSKUKey,
          voteAlign: row?.voteAlign || true,
          decision: row?.decision,
          decisionBy: email,
          ownerEmail: row?.ownerEmail,
          ownerName: row?.ownerName,
          effectiveDate: row?.effectiveDate,
          instructionComment: row?.instructionComment
        }));
      // Save the filtered data
      await saveDecisionData(editedData);
      setShowUndo(true);
      triggerNotification(
        `Decisions successfully submitted for ${editedRows?.length} SKUs`,
        'success'
      );
      dispatch(setEditedRows([]));
      setCheckedRows([]);
      setshowDecisonSubmit(true);
      setAreVotesApplied(true);

      // recalling the API after saving votes

      getUserLevelInfo(bu);
    } catch (error) {
      triggerNotification(error?.message, 'error');
      console.error('Error saving data:', error);
    }
  };
  const getTableColumns = (type) => {
    const updatedColumns = columns.map((column) => {
      let category = ''; // Default category

      if (perforamceMetric.includes(column.id)) {
        category = 'Performance Metrics';
      } else if (operationalInp.includes(column.id)) {
        category = 'Importance Inputs';
      } else if (decisions.includes(column.id)) {
        category = 'Decisions';
      } else if (productAttr.includes(column.id)) {
        category = 'Product Attributes';
      }

      return {
        ...column,
        category
      };
    });

    if (type === 'decision') {
      return isAdmin && isDecision ? [...updatedColumns, ...expandCols] : updatedColumns;
    } else if (type === 'completion') {
      let filteredColumns = updatedColumns?.filter((item) => item.id !== 'decision'
        && item.id !== 'effective date' && item.id !== 'owner' && item.id !== 'comment');
      return isAdmin && isCompleted ? [...filteredColumns, ...completionExpandCols] : updatedColumns;
    } else {
      return updatedColumns;
    }
  };

  const saveCompletionDataRows = async () => {
    try {
      // Filter the votingTableData based on CycleSKUKey present in editedRows
      const editedData = votingTableData
        .filter((row) => editedRows.includes(row.CycleSKUKey))
        .map((row) => ({
          cycleSKUKey: row.CycleSKUKey,
          completionStatus: row.completionStatus,
          execDate: row.ExecDate,
          finalComment: row.finalComment || ''
        }));

      // Save the filtered data
      await saveCompletionData(editedData);
      triggerNotification(
        `Decisions successfully submitted for ${editedRows?.length} SKUs`,
        'success'
      );
      dispatch(setEditedRows([]));
      setCheckedRows([]);
      setShowUndo(true);
      setshowDecisonSubmit(true);
      setAreVotesApplied(true);
      // recalling the API after saving votes
      getUserLevelInfo(bu);
    } catch (error) {
      const errorMessage = error?.response?.data || 'Failed to get data!';
      triggerNotification(errorMessage, 'error');
      dispatch(setDecisionVotingTableData(prevTableData));
      dispatch(setEditedRows([]));
      setCheckedRows([]);
    }
  };
  const saveData = () => {
    if (isCompleted) {
      saveCompletionDataRows();
    } else {
      saveDecisionDataRows();
    }
  };

  useEffect(() => {
    // First, call getFileUploadStatus immediately
    if (userDetails?.email !== '') {
      getFileUploadStatus();
    }
  }, [userDetails?.email]);

  useEffect(() => {
    if (Object.entries(uploadStatusMsg).length) {
      // Only set interval if it's not already set
      if (!intervalIdRef.current) {
        intervalIdRef.current = setInterval(() => {
          console.log('Calling getFileUploadStatus from interval...');
          getFileUploadStatus();
        }, 30000); // 30 seconds interval
      }
    } else {
      // If `uploadStatusMsg` is empty, clear the interval
      if (intervalIdRef.current) {
        console.log('Clearing interval because uploadStatusMsg is empty...');
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    }
    // Cleanup: Clear the interval when the component unmounts
    return () => {
      if (intervalIdRef.current) {
        console.log('Cleanup: Clearing interval...');
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, [uploadStatusMsg]);

  const getFileUploadStatusByUserID = async (user, bu) => {
    try {
      let response = await fetchUploadsInProgressByUserId(user, bu);

      setUploadStatusMsg({});
      if (response) {
        if (response?.status === 'Success') {
          getUserLevelInfo(bu);
          triggerNotification(response?.message + ' ' + response?.description, 'success');
        } else if (response?.status === 'Failed') {
          triggerNotification(response?.message + ' ' + response?.description, 'error');
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const getFileUploadStatus = async () => {
    try {
      let response = await fetchUploadsInProgress(bu);
      setUploadStatusMsg(response);

      if (Object.entries(response).length == 0) {
        getFileUploadStatusByUserID(userDetails.email, bu);

        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null; // Clear the reference as well
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const setCallReset = () => {
    return getUserLevelInfo(bu);
  };

  const getFilteredCustomerList = (data, selectedFilters) => {
    let config = [];
    if (isDecision) {
      config = finalDecisions;
    } else {
      config = decisionCentral;
    }
    let filters = { ...selectedFilters };
    if (selectedFilters.level_attribute_name?.length > 0) {
      delete filters.level_attribute_name;
    }
    const filteredData = data.filter((item) => {
      return config.every(({ key }) => {
        const selectedValuesForKey = filters[key] || [];
        const normalizedItemValue =
          key === 'votes aligned'
            ? item[key] === true
              ? 'Yes'
              : item[key] === false
                ? 'No'
                : ''
            : item[key];

        return (
          selectedValuesForKey.length === 0 ||
          (Array.isArray(normalizedItemValue)
            ? selectedValuesForKey.some((val) => normalizedItemValue.includes(val)) // Check for intersection
            : selectedValuesForKey.includes(normalizedItemValue)) // Direct match for non-array fields
        );
      });
    });
    return filteredData;
  };

  return (
    <Stack gap="16px" sx={{ padding: '24px', width: '100%' }}>
      <PageHeader
        pageHeader={'Decision Making Central'}
        showBu={false}
        showCycle={true}
        cardHeaderDetails={{
          cycleName: selectedCycle
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Stack direction="column" gap="1rem" style={{ maxWidth: '60%' }} alignItems={'center'}>
          <Typography sx={{ margin: '0', fontSize: '28px', lineHeight: '40px', fontWeight: '700' }}>
            Hey {userDetails.username}!
          </Typography>
          <Typography sx={{ fontSize: '16px', fontWeight: 500, lineHeight: '24px' }}>
            It’s time to submit the decisions for the SKU’s. You can select multiple SKU’s when
            applying to them the same criteria.
          </Typography>
        </Stack>
        <Stack>
          <img src={decisionMakingIcon} alt="Decision Making Icon" height={119} width={180} />
        </Stack>
      </div>

      <Stack>
        <VotingFilter
          isTableLoading={isTableLoading}
          tableName="decision table"
          saveVotes={saveData}
          votingTable="Decison Table"
          isAdmin={isAdmin}
          isFiledsEmpty={isAnyFieldEmpty}
          setCallReset={() => setCallReset()}
          setCallApply={() => {}}
          setSelectedFilters={(filters) => {
            setSelectedFilters(filters);
          }}
          selectedFilters={selectedFilters}
        />{' '}
      </Stack>

      <Stack alignItems={'center'}>
        {isCompleted && (
          <MultiVotingCard
            checkedRows={checkedRows}
            setVote={(vote) => setVote(vote)}
            multiVoteComponentLabels={multiVoteCompleteComponentLabels}
            parentComponent={'Completion Status'}
            getVoteData={getVoteCompletionData}
            resetVoteData={() => {
              resetVoteData();
            }}
            areVotesSaved={areVotesApplied}
            setAreVotesSaved={(areVotesApplied) => setAreVotesSaved(areVotesApplied)}
            recommendationOptions={recommendationOptions}
            tagUsers={tagDecisionUsers || []}
            isSingleSelected
            setShowApply={(showApply) => setShowApply(showApply)}
            setShowUndo={(showUndo) => setShowUndo(showUndo)}
            showApply={showApply}
            showUndo={showUndo}
            showDecisonSubmit={showDecisonSubmit}
            setshowDecisonSubmit={(showDecisonSubmit) => setshowDecisonSubmit(showDecisonSubmit)}
          />
        )}

        {isDecision && (
          <MultiVotingCard
            checkedRows={checkedRows}
            setVote={(vote) => setVote(vote)}
            multiVoteComponentLabels={multiVoteComponentLabels}
            parentComponent={'Decision'}
            getVoteData={getVoteData}
            resetVoteData={() => {
              resetVoteData();
            }}
            areVotesSaved={areVotesApplied}
            setAreVotesSaved={(areVotesApplied) => setAreVotesSaved(areVotesApplied)}
            recommendationOptions={recommendationOptions}
            tagUsers={tagDecisionUsers || []}
            isSingleSelected
            setShowApply={(showApply) => setShowApply(showApply)}
            setShowUndo={(showUndo) => setShowUndo(showUndo)}
            showApply={showApply}
            showUndo={showUndo}
            showDecisonSubmit={showDecisonSubmit}
            setshowDecisonSubmit={(showDecisonSubmit) => setshowDecisonSubmit(showDecisonSubmit)}
          />
        )}
      </Stack>
      <DecisionTable
        data={getFilteredCustomerList(votingTableData, selectedFilters)}
        columns={
          isDCMCompleted
            ? getTableColumns('finalView')
            : isCompleted
              ? getTableColumns('completion')
              : getTableColumns('decision')
        }
        isTableLoading={isTableLoading}
        checkedRows={checkedRows}
        setCheckedRows={setCheckedRows}
        vote={vote}
        isCompleted={isCompleted}
        isDecision={isDecision}
        tableName={isCompleted ? 'Completion Table' : 'Decision Table'}
        showCheckbox={isAdmin && isDecision ? isDecision : isCompleted}
        currentVotingTeam={isCompleted ? 'Completion Team' : 'Decision Team'}
        setUploadStatusMsg={(data) => setUploadStatusMsg(data)}
        currentActiveCycle={selectedCycle}
        currentActiveCycleID={selectedCycleId}
        userLevelUpload={userLevelUpload}
        userType={roles[0]}
        isUploadDownloadDisabled={
          showUploadDownload || Object.entries(uploadStatusMsg).length !== 0
        }
      />

      {/* {!showVotingViewScreen && ( */}
      <>
        <Box
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            backgroundColor: '#D7E5F8',
            padding: '15px 50px',
            borderRadius: '8px',
            boxShadow: '0px 4px 4px 0px #00000040',
            position: 'sticky',
            bottom: 1,
            zIndex: '100'
          }}>
          <Tooltip title="If done voting, update your calendar tasks here">
            <Button
              id="save-filter"
              variant="outlined"
              color="primary"
              size="medium"
              sx={{
                height: '32px',
                width: '175px'
              }}
              onClick={() =>
                navigate(
                  getUrlWithParams('/readiness/calendar', bu, selectedCycle, selectedCycleId)
                )
              }>
              Go To Calendar
            </Button>
          </Tooltip>
          <Button
            id="save-filter"
            variant="contained"
            color="primary"
            size="medium"
            disabled={editedRows?.length === 0 || (!isAdmin && isDecision) || isAnyFieldEmpty || isCompleted}
            sx={{
              height: '32px',
              width: '175px',
              marginLeft: '5%'
            }}
            onClick={saveData}>
            Save
          </Button>
        </Box>
      </>
    </Stack>
  );
};

export default DecisionMakingCentral;
